import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { ApiError, api, fetchBlob, API_BASE } from '../lib/api';
import { PdfView, boxStyle } from '../components/PdfView';
import { SignaturePad } from '../components/SignaturePad';
import type { Adoption } from '../components/SignaturePad';
import { Banner, Field, Loading, Modal } from '../components/ui';
import { BRAND_NAME, BrandMark } from '../components/Layout';

type Stage = 'verify' | 'consent' | 'fill' | 'sign' | 'done' | 'declined'
  | 'offline' | 'view';

/**
 * Recipient signing journey. No account required; designed phone-first.
 *
 * Stages map to the product's evidence requirements:
 *   verify  → authentication outcome recorded
 *   consent → disclosure shown + demonstrated ability to open the PDF
 *   fill    → the signer's own fields only
 *   sign    → explicit adoption, then a separate final signing action
 */
export function Sign() {
  const { token = '' } = useParams();
  return <SignExperience token={token} />;
}

/**
 * The signing journey itself.
 *
 * Shared by the hosted route and the embedded frame so there is exactly one
 * implementation: an embedded signer meets the same consent, authentication
 * and evidence controls, which is what the PRD requires.
 */
export function SignExperience({ token, embedded = false, readOnly: readOnlyProp,
                                 initialSession }: {
  token: string; embedded?: boolean; readOnly?: boolean;
  /** Supplied by an embedded read-only session, which needs no passcode. */
  initialSession?: string;
}) {
  const [view, setView] = useState<any>(null);
  const [session, setSession] = useState<string | null>(initialSession ?? null);
  const [stage, setStage] = useState<Stage>('verify');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const [challenge, setChallenge] = useState<any>(null);
  const [code, setCode] = useState('');

  const [accessReceipt, setAccessReceipt] = useState<string | null>(null);
  // Issued at signing, read-only, and the only credential that still
  // works afterwards: the signing session is retired the moment the
  // signature is recorded.
  const [viewSession, setViewSession] = useState<string | null>(null);
  // The delivered PDF, rendered in this page rather than handed to the
  // browser's viewer in another tab.
  const [reviewUrl, setReviewUrl] = useState<string | null>(null);
  const [consentChecked, setConsentChecked] = useState(false);
  const [showDisclosure, setShowDisclosure] = useState(false);

  const [values, setValues] = useState<Record<string, any>>({});
  const [adoption, setAdoption] = useState<Adoption | null>(null);
  const [outstanding, setOutstanding] = useState<any[]>([]);
  const [readOnly, setReadOnly] = useState(false);
  const [declineOpen, setDeclineOpen] = useState(false);
  const [declineReason, setDeclineReason] = useState('');
  const [offlineOpen, setOfflineOpen] = useState(false);
  const [offlineNote, setOfflineNote] = useState('');
  const fieldRefs = useRef<Record<string, HTMLElement | null>>({});
  // Which field the signer is on, so Next continues from where they are
  // rather than from wherever the buttons last left off.
  const [activeField, setActiveField] = useState(0);

  // The fields this signer actually completes. Signature and initials are
  // applied by adopting a mark, not typed into the page.
  const fillable = useMemo<any[]>(
    () => (view?.fields || []).filter(
      (f: any) => !['signature', 'initials'].includes(f.kind)),
    [view]);

  /**
   * What is still required, computed live.
   *
   * `outstanding` only arrives from the server after a save is rejected, so on
   * its own it shows nothing until the signer has already hit a wall. This
   * mirrors the same rule locally for the progress bar, and unions in whatever
   * the server last said so the two can never disagree in a way that strands
   * somebody.
   */
  const remaining = useMemo<any[]>(() => {
    const isBlank = (value: any) =>
      value === undefined || value === null || value === '' || value === false;
    const local = fillable.filter(
      (f: any) => f.required && isBlank(values[f.id]));
    const byId = new Map<string, any>(
      local.map((f: any) => [f.id, { field_id: f.id, label: f.label, page: f.page }]));
    for (const item of outstanding) {
      if (!byId.has(item.field_id) && isBlank(values[item.field_id])) {
        byId.set(item.field_id, item);
      }
    }
    return [...byId.values()];
  }, [fillable, values, outstanding]);

  /**
   * Every field assigned to this signer, in reading order.
   *
   * Page first, then down the page, then across. The PDF origin is
   * bottom-left, so "down the page" is descending y -- sorting ascending here
   * would walk the document upwards and make Next feel random.
   */
  const orderedFields = useMemo<any[]>(() => {
    const rows = [...(view?.fields || [])];
    rows.sort((a: any, b: any) => (
      a.page - b.page
      || (b.y + b.height) - (a.y + a.height)
      || a.x - b.x));
    return rows;
  }, [view]);

  const focusField = useCallback((index: number) => {
    const field = orderedFields[index];
    if (!field) return;
    setActiveField(index);
    const node = fieldRefs.current[field.id];
    node?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    node?.focus?.({ preventScroll: true });
  }, [orderedFields]);

  const headers = useMemo<Record<string, string>>(
    () => (session ? { 'x-signing-session': session } : ({} as Record<string, string>)),
    [session],
  );

  const reload = useCallback(async (sessionToken?: string | null) => {
    const data = await api.get(`/sign/${token}`, {
      headers: sessionToken ? { 'x-signing-session': sessionToken } : {},
    });
    setView(data);
    return data;
  }, [token]);

  useEffect(() => { setReadOnly(Boolean(readOnlyProp)); }, [readOnlyProp]);
  useEffect(() => { if (initialSession) setSession(initialSession); }, [initialSession]);

  useEffect(() => {
    reload().catch((cause) => {
      setError(cause instanceof ApiError ? cause.message : 'This link could not be opened.');
    });
  }, [reload]);

  useEffect(() => {
    if (!view) return;
    // A read-only session goes straight to the document: there is nothing to
    // verify or consent to, because there is nothing it can do.
    if (readOnly) { setStage('view'); return; }
    if (view.recipient.status === 'completed') { setStage('done'); return; }
    if (view.recipient.status === 'declined') { setStage('declined'); return; }
    if (!session) { setStage('verify'); return; }
    if (!view.consent.accepted) { setStage('consent'); return; }
    setStage((current) => (current === 'verify' || current === 'consent' ? 'fill' : current));
  }, [view, session]);

  useEffect(() => {
    if (view?.fields) {
      const seeded: Record<string, any> = {};
      for (const field of view.fields) {
        if (field.value != null) seeded[field.id] = field.value;
        if (field.kind === 'date' && !field.value) {
          seeded[field.id] = new Date().toISOString().slice(0, 10);
        }
      }
      setValues((current) => ({ ...seeded, ...current }));
    }
  }, [view?.fields]); // eslint-disable-line react-hooks/exhaustive-deps

  // ----------------------------------------------------------- auth stage
  const startAuth = async () => {
    setBusy(true); setError(null);
    try {
      const result = await api.post(`/sign/${token}/auth/start`);
      if (result.verified && result.session_token) {
        setSession(result.session_token);
        await reload(result.session_token);
      } else {
        setChallenge(result);
      }
    } catch (cause) {
      setError(cause instanceof ApiError ? cause.message : 'Could not start verification.');
    } finally { setBusy(false); }
  };

  const verifyCode = async (event: React.FormEvent) => {
    event.preventDefault();
    setBusy(true); setError(null);
    try {
      const result = await api.post(`/sign/${token}/auth/verify`, {
        challenge_id: challenge.challenge_id, code,
      });
      setSession(result.session_token);
      await reload(result.session_token);
      setCode('');
    } catch (cause) {
      setError(cause instanceof ApiError ? cause.message : 'That code did not work.');
    } finally { setBusy(false); }
  };

  // -------------------------------------------------------- consent stage
  const openAttachment = async (documentId: string) => {
    setBusy(true); setError(null);
    try {
      const { blob } = await fetchBlob(
        `/sign/${token}/document?document_id=${encodeURIComponent(documentId)}`,
        headers);
      const url = URL.createObjectURL(blob);
      window.open(url, '_blank', 'noopener');
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch (cause) {
      setError(cause instanceof ApiError ? cause.message
                                         : 'That attachment could not be opened.');
    } finally { setBusy(false); }
  };

  /**
   * Show the delivered PDF, and record that it opened.
   *
   * C02 needs more than a ticked box: the signer has to demonstrate they can
   * actually read the format the agreement is delivered in. That requirement
   * says nothing about *where* it renders, and sending them to a separate tab
   * made the consent screen hard to get back to -- especially on a phone,
   * where the browser's PDF viewer takes over the window.
   *
   * So it renders here instead. Still an explicit action the signer takes,
   * still the real delivered bytes, still a single-use receipt minted from
   * that fetch -- the page simply no longer disappears underneath them.
   */
  const reviewDocument = async () => {
    setBusy(true); setError(null);
    try {
      const { blob, headers: responseHeaders } = await fetchBlob(
        `/sign/${token}/document?demonstrate=1`, headers);
      setAccessReceipt(responseHeaders.get('x-access-receipt'));
      setReviewUrl((previous) => {
        if (previous) URL.revokeObjectURL(previous);
        return URL.createObjectURL(blob);
      });
      await api.post(`/sign/${token}/consent/presented`, undefined, { headers });
    } catch (cause) {
      setError(cause instanceof ApiError ? cause.message
                                         : 'The document could not be opened.');
    } finally { setBusy(false); }
  };

  /** For anyone who would rather have the file. Keeps the paper-copy route
   *  honest without making it the only way to satisfy the demonstration. */
  const downloadDelivered = async () => {
    try {
      const { blob } = await fetchBlob(
        `/sign/${token}/document?download=1`, headers);
      const url = URL.createObjectURL(blob);
      const anchor = document.createElement('a');
      anchor.href = url;
      anchor.download = `${view?.envelope?.title ?? 'agreement'}.pdf`;
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch {
      setError('The download could not be started.');
    }
  };

  // Release the blob when it is no longer on screen.
  useEffect(() => () => { if (reviewUrl) URL.revokeObjectURL(reviewUrl); },
           [reviewUrl]);

  const openSignedCopy = async () => {
    setBusy(true); setError(null);
    try {
      const { blob } = await fetchBlob(`/sign/${token}/signed-copy`,
        viewSession ? { 'x-signing-session': viewSession } : {});
      const url = URL.createObjectURL(blob);
      window.open(url, '_blank', 'noopener');
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch (cause) {
      setError(cause instanceof ApiError ? cause.message
                                         : 'The document could not be opened.');
    } finally { setBusy(false); }
  };

  const acceptConsent = async () => {
    setBusy(true); setError(null);
    try {
      await api.post(`/sign/${token}/consent`,
        { accepted: true, access_receipt: accessReceipt }, { headers });
      await reload(session);
      setStage('fill');
    } catch (cause) {
      setError(cause instanceof ApiError ? cause.message : 'Consent could not be recorded.');
    } finally { setBusy(false); }
  };

  // ----------------------------------------------------------- fill stage
  const saveValues = async () => {
    setBusy(true); setError(null);
    try {
      const result = await api.put(`/sign/${token}/values`, { values }, { headers });
      setOutstanding(result.required_outstanding);
      return result.required_outstanding as any[];
    } catch (cause) {
      setError(cause instanceof ApiError ? cause.message : 'Your entries could not be saved.');
      return null;
    } finally { setBusy(false); }
  };

  const goToSign = async () => {
    const remaining = await saveValues();
    if (remaining === null) return;
    const blocking = remaining.filter((f) => !['signature', 'initials'].includes(f.kind));
    if (blocking.length) {
      const first = blocking[0];
      fieldRefs.current[first.field_id]?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      fieldRefs.current[first.field_id]?.focus?.();
      setError(`“${first.label}” still needs your input.`);
      return;
    }
    setError(null);
    setStage('sign');
  };

  const finish = async () => {
    if (!adoption) return;
    setBusy(true); setError(null);
    try {
      const result = await api.post(`/sign/${token}/complete`,
        { signature: adoption, values }, { headers });
      // The signing session is retired by that call. This read-only one is
      // issued in its place and is what lets the signer re-open the document
      // they just signed.
      setViewSession(result?.view_session ?? null);
      await reload(session);
      setStage('done');
    } catch (cause) {
      if (cause instanceof ApiError && cause.code === 'required_fields_missing') {
        setOutstanding((cause.detail as any)?.fields || []);
        setStage('fill');
      }
      setError(cause instanceof ApiError ? cause.message : 'Signing could not be completed.');
    } finally { setBusy(false); }
  };

  const decline = async () => {
    setBusy(true);
    try {
      await api.post(`/sign/${token}/decline`, { reason: declineReason }, { headers });
      setDeclineOpen(false);
      setStage('declined');
    } catch (cause) {
      setError(cause instanceof ApiError ? cause.message : 'Could not record your decline.');
    } finally { setBusy(false); }
  };

  const requestOffline = async () => {
    setBusy(true);
    try {
      await api.post(`/sign/${token}/offline`, { note: offlineNote }, { headers });
      setOfflineOpen(false);
      setStage('offline');
    } catch (cause) {
      setError(cause instanceof ApiError ? cause.message : 'Could not send your request.');
    } finally { setBusy(false); }
  };

  if (error && !view) {
    return (
      <SignFrame embedded={embedded}>
        <Banner tone="danger" title="This link cannot be opened">{error}</Banner>
      </SignFrame>
    );
  }
  if (!view) {
    return (
      <SignFrame embedded={embedded}>
        <Loading label="Opening your agreement…" />
      </SignFrame>
    );
  }

  const org = view.envelope.sender_organization;
  const brandStyle = org.brand_color
    ? ({ '--brand': org.brand_color } as React.CSSProperties) : undefined;

  const stages: { key: Stage; label: string }[] = [
    { key: 'verify', label: 'Verify' },
    { key: 'consent', label: 'Consent' },
    { key: 'fill', label: 'Complete' },
    { key: 'sign', label: 'Sign' },
  ];
  const stageIndex = stages.findIndex((s) => s.key === stage);

  return (
    <SignFrame orgName={org.name} style={brandStyle} embedded={embedded}>
      <div className="stack">
        <header className="stack-s">
          <h1 style={{ margin: 0, fontSize: 'clamp(1.2rem, 1.05rem + 1vw, 1.6rem)' }}>
            {view.envelope.title}
          </h1>
          <p className="muted small" style={{ margin: 0 }}>
            {org.name} sent this to {view.recipient.email_masked}
          </p>
          {stageIndex >= 0 && stage !== 'done' && !readOnly && (
            <ol className="steps" aria-label="Signing progress">
              {stages.map((item, index) => (
                <li key={item.key}
                    className={`step${index === stageIndex ? ' step--active' : ''}${index < stageIndex ? ' step--done' : ''}`}>
                  <span className="step__num" aria-hidden="true">
                    {index < stageIndex ? '✓' : index + 1}
                  </span>
                  {item.label}
                  {index < stages.length - 1 && <span className="step__sep" aria-hidden="true">›</span>}
                </li>
              ))}
            </ol>
          )}
        </header>

        {error && <Banner tone="danger" title="Please check this">{error}</Banner>}

        {readOnly && (
          <Banner tone="info" title="Read-only view">
            This is a preview of the agreement. To sign it, open the signing link
            sent to you.
          </Banner>
        )}

        {stage === 'verify' && !readOnly && (
          <div className="card stack">
            <h2 style={{ margin: 0, fontSize: '1.1rem' }}>Verify it is you</h2>
            {!challenge ? (
              <>
                <p className="muted small" style={{ margin: 0 }}>
                  {view.authentication.required_method === 'email_link'
                    ? 'This link was sent to you personally. Continue to review the agreement.'
                    : `We will send a one-time code to the ${
                        view.authentication.required_method === 'sms_otp'
                          ? 'mobile number' : 'email address'} on file.`}
                </p>
                <button className="btn btn--primary btn--large btn--block"
                        disabled={busy} onClick={() => void startAuth()}>
                  {busy ? 'Working…'
                        : view.authentication.required_method === 'email_link'
                          ? 'Continue' : 'Send my code'}
                </button>
                <p className="small muted" style={{ margin: 0 }}>
                  {view.authentication.meaning}
                </p>
              </>
            ) : (
              <form className="stack" onSubmit={verifyCode}>
                <p className="muted small" style={{ margin: 0 }}>
                  We sent a {challenge.method === 'sms_otp' ? 'text' : 'email'} to{' '}
                  <strong>{challenge.destination_mask}</strong>. Enter the code below.
                </p>
                <Field label="One-time code" id="otp" required>
                  <input id="otp" className="input" inputMode="numeric" autoComplete="one-time-code"
                         maxLength={6} pattern="[0-9]{6}" required value={code}
                         style={{ fontSize: '1.4rem', letterSpacing: '.3em', textAlign: 'center' }}
                         onChange={(event) => setCode(event.target.value.replace(/\D/g, ''))} />
                </Field>
                <button className="btn btn--primary btn--large btn--block"
                        disabled={busy || code.length !== 6}>
                  {busy ? 'Checking…' : 'Verify and continue'}
                </button>
                <button type="button" className="btn btn--ghost btn--block" disabled={busy}
                        onClick={() => { setChallenge(null); void startAuth(); }}>
                  Send a new code
                </button>
              </form>
            )}
          </div>
        )}

        {stage === 'consent' && (
          <div className="card stack">
            <h2 style={{ margin: 0, fontSize: '1.1rem' }}>Consent to sign electronically</h2>
            {!accessReceipt ? (
              <>
                <p className="muted small" style={{ margin: 0 }}>
                  Have a look at the agreement below, then confirm you are happy
                  to sign it electronically.
                </p>
                <button className="btn btn--primary btn--large btn--block"
                        disabled={busy} onClick={() => void reviewDocument()}>
                  {busy ? 'Loading…' : 'Show me the agreement'}
                </button>
              </>
            ) : (
              <p className="muted small" style={{ margin: 0 }}>
                This is the agreement you are being asked to sign. Scroll through
                it, then confirm below.{' '}
                <button type="button" className="linkbutton"
                        onClick={() => void downloadDelivered()}>
                  Download a copy
                </button>
              </p>
            )}

            {/* Rendered in the page, not in another tab. The signer keeps the
                consent controls in front of them, which is the whole point --
                the demonstration was already satisfied by the fetch above. */}
            {reviewUrl && (
              <div className="reviewpane">
                <PdfView url={reviewUrl} pageSizes={view.document.page_sizes}
                         maxWidth={820} />
              </div>
            )}

            <div>
              <button type="button" className="btn btn--small btn--ghost"
                      aria-expanded={showDisclosure}
                      onClick={() => setShowDisclosure((current) => !current)}>
                {showDisclosure ? 'Hide' : 'Read'} the full electronic records disclosure
              </button>
              {showDisclosure && (
                <div className="disclosure" tabIndex={0} style={{ marginTop: 10 }}>
                  {view.consent.package.disclosure_text}
                </div>
              )}
            </div>

            <label className="checkbox">
              <input type="checkbox" checked={consentChecked}
                     disabled={!accessReceipt}
                     onChange={(event) => setConsentChecked(event.target.checked)} />
              <span className="checkbox__text">{view.consent.package.consent_text}</span>
            </label>
            {!accessReceipt && (
              <p className="muted small" style={{ margin: 0 }}>
                Open the agreement above to continue — electronic-signature
                rules need you to have seen the document itself, not just this
                page.
              </p>
            )}

            <button className="btn btn--primary btn--large btn--block"
                    disabled={busy || !consentChecked || !accessReceipt}
                    onClick={() => void acceptConsent()}>
              {busy ? 'Recording…' : 'I consent — continue'}
            </button>

            <div className="row row--between">
              <button type="button" className="btn btn--small btn--ghost"
                      onClick={() => setOfflineOpen(true)}>
                Request paper or offline handling
              </button>
              <button type="button" className="btn btn--small btn--ghost"
                      onClick={() => setDeclineOpen(true)}>
                Decline to sign
              </button>
            </div>
          </div>
        )}

        {(stage === 'fill' || stage === 'sign' || stage === 'view') && (
          <div className="stack">
            {stage === 'fill' && (
              /* A bar, not a form. The inputs live on the document itself; what
                 belongs up here is what is left to do and how to reach it.
                 Asking for the same values in a detached list meant answering
                 questions with no sight of what they referred to. */
              <div className="card stack-s">
                <div className="row row--between" style={{ flexWrap: 'wrap', gap: 10 }}>
                  <div style={{ minWidth: 0 }}>
                    <h2 style={{ margin: 0, fontSize: '1.02rem' }}>
                      {fillable.length === 0
                        ? 'Nothing to fill in'
                        : remaining.length === 0
                          ? 'Your fields are complete'
                          : `${fillable.length - remaining.length} of ${fillable.length} fields complete`}
                    </h2>
                    <p className="card__sub" style={{ margin: '2px 0 0' }}>
                      {fillable.length === 0
                        ? 'Continue to sign.'
                        : 'Fill your fields on the document below — they are highlighted.'}
                    </p>
                  </div>
                  <div className="row row--tight">
                    {orderedFields.length > 1 && (
                      /* Stepping through the fields, in reading order. With
                         several scattered over a long document, hunting for
                         the next one is the slow part of signing. */
                      <span className="row row--tight fieldnav">
                        <button type="button" className="btn btn--small btn--ghost"
                                aria-label="Previous field"
                                disabled={activeField <= 0}
                                onClick={() => focusField(activeField - 1)}>
                          ←
                        </button>
                        <span className="small muted fieldnav__count">
                          Field {Math.min(activeField + 1, orderedFields.length)} of{' '}
                          {orderedFields.length}
                        </span>
                        <button type="button" className="btn btn--small btn--ghost"
                                aria-label="Next field"
                                disabled={activeField >= orderedFields.length - 1}
                                onClick={() => focusField(activeField + 1)}>
                          →
                        </button>
                      </span>
                    )}
                    <button type="button" className="btn btn--small btn--ghost"
                            onClick={() => setDeclineOpen(true)}>
                      Decline to sign
                    </button>
                    <button className="btn btn--primary" disabled={busy}
                            onClick={() => void goToSign()}>
                      {busy ? 'Saving…' : 'Continue to sign'}
                    </button>
                  </div>
                </div>

                {remaining.length > 0 && (
                  /* Required-field navigation (F08): each jumps to the field on
                     the page instead of restating it here. */
                  <div className="row row--tight" style={{ flexWrap: 'wrap' }}>
                    <span className="small muted">Still needed:</span>
                    {remaining.map((item: any) => (
                      <button key={item.field_id} type="button"
                              className="btn btn--small btn--ghost"
                              onClick={() => {
                                const index = orderedFields.findIndex(
                                  (f: any) => f.id === item.field_id);
                                if (index >= 0) focusField(index);
                              }}>
                        {item.label}
                        <span className="muted"> · p{(item.page ?? 0) + 1}</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}

            {stage === 'sign' && (
              <div className="card stack">
                <h2 style={{ margin: 0, fontSize: '1.05rem' }}>Adopt your signature</h2>
                <SignaturePad defaultName={view.recipient.name} value={adoption}
                              onChange={setAdoption} />
                <Banner tone="info">
                  Signing applies your adopted mark to every signature and initials
                  field assigned to you, and records your intent against this exact
                  version of the document.
                </Banner>
                <div className="row row--between">
                  <button type="button" className="btn btn--ghost"
                          onClick={() => setStage('fill')}>Back</button>
                  <button className="btn btn--primary btn--large"
                          disabled={busy || !adoption} onClick={() => void finish()}>
                    {busy ? 'Signing…' : 'Sign the agreement'}
                  </button>
                </div>
              </div>
            )}

            {view.attachments?.length > 0 && (
              <div className="card stack-s">
                <h2 style={{ margin: 0, fontSize: '1.02rem' }}>
                  Attached documents
                </h2>
                <p className="card__sub" style={{ margin: 0 }}>
                  These form part of what you are agreeing to. They carry no
                  fields to complete.
                </p>
                <div className="list">
                  {view.attachments.map((item: any) => (
                    <button key={item.document_id} type="button"
                            className="btn btn--block"
                            style={{ justifyContent: 'space-between' }}
                            disabled={busy}
                            onClick={() => void openAttachment(item.document_id)}>
                      <span className="truncate">{item.name}</span>
                      <span className="muted small">
                        {item.page_count} page{item.page_count === 1 ? '' : 's'} ↗
                      </span>
                    </button>
                  ))}
                </div>
              </div>
            )}

            <div className="card card--pad-0" style={{ overflow: 'hidden' }}>
              <div style={{ padding: '14px 16px 0' }}>
                <h2 style={{ margin: 0, fontSize: '1.02rem' }}>The agreement</h2>
                <p className="card__sub">
                  This is the exact version you are signing. Your fields are
                  highlighted.
                </p>
              </div>
              <div style={{ padding: 14 }}>
                <PdfView
                  url={`${API_BASE}/sign/${token}/document`}
                  headers={headers}
                  pageSizes={view.document.page_sizes}
                  renderOverlay={(pageIndex, size) => (
                    <>
                      {view.other_fields.filter((f: any) => f.page === pageIndex)
                        .map((field: any) => (
                          <div key={field.id} className="fieldbox fieldbox--other"
                               style={boxStyle(field, size)}>
                            <span className="fieldbox__value">{field.label}</span>
                          </div>
                        ))}
                      {view.fields.filter((f: any) => f.page === pageIndex).map((field: any) => {
                        const isSig = ['signature', 'initials'].includes(field.kind);
                        const filled = isSig
                          ? Boolean(adoption)
                          : values[field.id] !== undefined && values[field.id] !== ''
                            && values[field.id] !== false;
                        const position = orderedFields.findIndex(
                          (f: any) => f.id === field.id);
                        const cls = `fieldbox${filled ? ' fieldbox--done'
                          : field.required ? ' fieldbox--required-missing' : ''}`
                          + (position === activeField ? ' fieldbox--active' : '');
                        // Clicking or tabbing into a field is also a way of
                        // saying "I am here", so Next continues from it.
                        const claim = () => { if (position >= 0) setActiveField(position); };

                        // Fillable fields are edited where they sit on the
                        // page. Asking for the same values again in a separate
                        // list above the document meant answering questions
                        // with no sight of what they referred to.
                        if (stage === 'fill' && !isSig) {
                          if (field.kind === 'checkbox') {
                            const on = values[field.id] === true
                              || values[field.id] === 'true';
                            return (
                              <button
                                key={field.id}
                                type="button"
                                aria-label={field.label}
                                aria-pressed={on}
                                ref={(node) => { fieldRefs.current[field.id] = node; }}
                                className={`${cls} fieldbox--input`}
                                style={boxStyle(field, size)}
                                onFocus={claim}
                                onClick={() => {
                                  claim();
                                  setValues({ ...values, [field.id]: !on });
                                }}
                              >
                                <span className="fieldbox__value" aria-hidden="true">
                                  {on ? '✓' : field.label}
                                </span>
                              </button>
                            );
                          }
                          if (field.kind === 'radio') {
                            return (
                              <select
                                key={field.id}
                                aria-label={field.label}
                                ref={(node) => { fieldRefs.current[field.id] = node; }}
                                className={`${cls} fieldbox--input fieldbox--control`}
                                style={boxStyle(field, size)}
                                onFocus={claim}
                                value={values[field.id] ?? ''}
                                onChange={(event) =>
                                  setValues({ ...values, [field.id]: event.target.value })}
                              >
                                <option value="">{field.label}</option>
                                {(field.options || []).map((option: any) => (
                                  <option key={option.value} value={option.value}>
                                    {option.label || option.value}
                                  </option>
                                ))}
                              </select>
                            );
                          }
                          return (
                            <input
                              key={field.id}
                              aria-label={field.label}
                              placeholder={field.label}
                              ref={(node) => { fieldRefs.current[field.id] = node; }}
                              className={`${cls} fieldbox--input fieldbox--control`}
                              style={boxStyle(field, size)}
                              onFocus={claim}
                              type={field.kind === 'date' ? 'date' : 'text'}
                              value={values[field.id] ?? ''}
                              onChange={(event) =>
                                setValues({ ...values, [field.id]: event.target.value })}
                            />
                          );
                        }

                        const text = isSig && adoption
                          ? (field.kind === 'initials'
                              ? adoption.name.split(/\s+/).map((p) => p[0]).join('').toUpperCase()
                              : adoption.name)
                          : field.kind === 'checkbox'
                            ? (filled ? '✓' : field.label)
                            : (values[field.id] || field.label);
                        return (
                          <div key={field.id}
                               ref={(node) => { fieldRefs.current[field.id] = node; }}
                               /* Focusable only programmatically: Next can land
                                  here to show where the signature goes, without
                                  adding a tab stop that does nothing. */
                               tabIndex={-1}
                               onFocus={claim}
                               className={`${cls}${isSig && adoption ? ' fieldbox--filled' : ''}`}
                               style={boxStyle(field, size)}>
                            <span className="fieldbox__value">{text}</span>
                          </div>
                        );
                      })}
                    </>
                  )}
                />
              </div>
            </div>
          </div>
        )}

        {stage === 'done' && (
          <div className="card stack center">
            <div style={{ fontSize: '2.4rem' }} aria-hidden="true">✓</div>
            <h2 style={{ margin: 0 }}>You have signed</h2>
            <p className="muted small" style={{ margin: 0 }}>
              Your signature was recorded for “{view.envelope.title}”.
            </p>
            {view.other_recipients.some((r: any) => r.status !== 'completed' && r.role === 'signer') ? (
              <Banner tone="info" title="Waiting for others">
                Other signers still need to complete this agreement. You will receive
                your copy by email once everyone has signed and the final document is
                ready.
              </Banner>
            ) : (
              <Banner tone="ok" title="Everyone has signed">
                The final document and its completion report are being prepared. Your
                copy will arrive by email shortly.
              </Banner>
            )}
            {viewSession ? (
              <button className="btn" disabled={busy}
                      onClick={() => void openSignedCopy()}>
                {busy ? 'Opening…' : 'Open the document you signed'}
              </button>
            ) : (
              /* No read-only session: this page was reloaded after signing, so
                 the one issued at signing is gone. Offering the button anyway
                 is what produced "your session expired" -- the link itself has
                 to be opened again. */
              <p className="muted small" style={{ margin: 0 }}>
                To see the document again, open your original link from the
                email. Your signed copy is sent to you once everyone has
                signed.
              </p>
            )}
          </div>
        )}

        {stage === 'declined' && (
          <div className="card stack center">
            <h2 style={{ margin: 0 }}>You declined to sign</h2>
            <p className="muted small" style={{ margin: 0 }}>
              {org.name} has been notified. Contact them directly if you want to
              discuss the agreement.
            </p>
          </div>
        )}

        {stage === 'offline' && (
          <div className="card stack center">
            <h2 style={{ margin: 0 }}>Offline handling requested</h2>
            <p className="muted small" style={{ margin: 0 }}>
              {org.name} has been asked to arrange a paper or offline alternative.
              Any signature you have already given is preserved.
            </p>
          </div>
        )}
      </div>

      {declineOpen && (
        <Modal title="Decline to sign" onClose={() => setDeclineOpen(false)}
               actions={<>
                 <button className="btn" onClick={() => setDeclineOpen(false)}>Cancel</button>
                 <button className="btn btn--danger"
                         disabled={busy || declineReason.trim().length < 3}
                         onClick={() => void decline()}>Decline</button>
               </>}>
          <p className="small muted">
            The sender is notified with your reason. This agreement will not be
            completed.
          </p>
          <Field label="Reason" id="decline-reason" required>
            <textarea id="decline-reason" className="textarea" value={declineReason}
                      onChange={(event) => setDeclineReason(event.target.value)} />
          </Field>
        </Modal>
      )}

      {offlineOpen && (
        <Modal title="Request paper or offline handling" onClose={() => setOfflineOpen(false)}
               actions={<>
                 <button className="btn" onClick={() => setOfflineOpen(false)}>Cancel</button>
                 <button className="btn btn--primary" disabled={busy}
                         onClick={() => void requestOffline()}>Send request</button>
               </>}>
          <p className="small muted">
            There is no fee to receive records on paper or to withdraw your consent.
            Withdrawing affects how future records reach you; it does not erase
            signatures you have already given.
          </p>
          <Field label="Anything the sender should know?" id="offline-note">
            <textarea id="offline-note" className="textarea" value={offlineNote}
                      onChange={(event) => setOfflineNote(event.target.value)} />
          </Field>
        </Modal>
      )}
    </SignFrame>
  );
}

function SignFrame({ children, orgName, style, embedded = false }: {
  children: React.ReactNode; orgName?: string; style?: React.CSSProperties;
  embedded?: boolean;
}) {
  return (
    <div className="page" style={style}>
      <a className="skip-link" href="#main">Skip to main content</a>
      {/* Inside a host application the surrounding chrome belongs to the host. */}
      {!embedded && <header className="topbar">
        <div className="shell shell--narrow topbar__inner">
          <span className="brand">
            <BrandMark />
            {orgName || BRAND_NAME}
          </span>
          <span className="spacer" />
          <span className="pill">Secure signing</span>
        </div>
      </header>}
      <main id="main" className="shell shell--narrow section" style={{ flex: 1 }}>
        {children}
      </main>
      <footer className="footer">
        <div className="shell shell--narrow">
          <p className="small" style={{ margin: 0 }}>
            Signing is powered by {BRAND_NAME}. Your signature and the actions you
            take are recorded against the exact version of this document shown
            above. No cryptographic PDF seal or independent timestamp is applied at
            this assurance level.
          </p>
        </div>
      </footer>
    </div>
  );
}
